/**
 * /dev/show6 — a 3D coverflow of TEAMS, in the chatteaser3 design language.
 *
 * The top tabs are the category filter (All / Ops / Cortex / Vitals / Arcade);
 * each card is one team (pulled from the same `useTeams()` hook the landing page
 * uses), rendered in one of several switchable layout variants. Every surface
 * keeps chatteaser3's vocabulary: pure-black canvas, single mint accent, frosted
 * glass with the signature bevel, oversized radii, mint corner-glows, two-tone
 * eyebrows, gradient-clipped text.
 *
 * Production: this module IS the teams gallery / team details / session
 * preflight surface (the old modals' replacement). /dev/show6 is its
 * tweaks-enabled mount.
 */

import type { CSSProperties } from "react";
import type { TargetAndTransition, Transition, Variants } from "framer-motion";
import { TeamCategory } from "@cabal/shared/domain/session/team/enums.js";
import {
  PORTAL_ROOT_CONTAINER_ID,
  PORTAL_ROOT_Z_INDEX,
} from "@/components/ui/Cportal";
import type { ContainerPreset } from "@/constants/componentPresets";
import { TEAM_LIST_CATEGORY, type TeamListCategory } from "@/contexts/teams";
import { zIndex } from "@/theme/zIndex";
import type { TeamData } from "../Cteamcard";
import { TeamsGalleryCollection } from "./gallery.collection";

// --- chatteaser3 design tokens (mirrored so show6 is self-describing) ---------

export const MINT = "#5FF3B8";
export const PAGE_BLACK = "#000000";

/** Price-tier semantics: a HIGH price is a cost, not a strength — cheap reads
 *  calm (mint), pricey reads hot (coral). Keyed by the catalog's 0–3 tier. */
export const PRICE_TIER_COLORS: Record<number, string> = {
  0: "rgba(255,255,255,0.35)",
  1: MINT,
  2: "#FFC24B",
  3: "#FF6E6E",
};

export function priceTierColor(priceTier: number | null): string {
  return PRICE_TIER_COLORS[priceTier ?? 0] ?? PRICE_TIER_COLORS[0];
}

// --- overlay z bands -----------------------------------------------------------
// The deck shell sits UNDER the morphing overlays; auth sits above everything.
// (App modal bands live around 300–500 — the gallery stack is deliberately above
// them, matching the old teams/details modals it replaced.)
export const GALLERY_DECK_Z = 900;
export const GALLERY_OVERLAY_Z = 1000;

/**
 * Overlay z for a gallery overlay (team overlay / member glimpse) given its
 * opener. A stacked opener (modalIndex ≥ 1 — opened above a rising modal)
 * must clear the #portal-root container ITSELF: rising modals render inside
 * that container, whose element carries z 9999 on <body>, so any body-level
 * overlay below 10000 paints BENEATH every rising modal regardless of its
 * own band. popoverAboveModal (10000) stays under the global message modal.
 */
export function galleryOverlayZIndex(modalIndex?: number): number {
  return modalIndex != null && modalIndex > 0
    ? zIndex.popoverAboveModal
    : GALLERY_OVERLAY_Z;
}
/** The deck shell's dedicated Cportal container (isolated from portal-root's 9999). */
export const GALLERY_DECK_PORTAL_CONTAINER_ID = "teams-gallery-deck-portal";

/**
 * WHICH portal container a deck shell mounts into.
 *
 * The deck's own container sits at GALLERY_DECK_Z on `<body>`, which is under
 * `#portal-root` (9999) — fine for every ordinary mount, and useless for the
 * one host that has to clear a rising modal: the team modal opened ABOVE
 * another modal lifts its surface to `popoverAboveModal` to escape that
 * container, and a picker raised from there would paint beneath the very modal
 * the team was opened over. There is no body-level band between 9999 and 10000
 * to squeeze into, so that picker mounts INSIDE the shared container instead
 * and orders itself against the modals stacked there — the same band the deck
 * always claimed (`GALLERY_DECK_Z`, deliberately above the 300–500 modal
 * bands), now read as an IN-CONTAINER order rather than a body-level one.
 *
 * That leaves the team surface numerically above the picker, which is exactly
 * what the picker's own contract already requires: raising it collapses the
 * surface to progress 0, where it is transparent and pointer-inert
 * (`surfaceVisible` / `surfaceOwnsInput`), so the layer above it is not a
 * layer at all while the deck is up.
 */
export enum TeamsGalleryDeckLayer {
  /** The deck's own body-level container — every ordinary mount. */
  Deck = "Deck",
  /** Inside the shared modal container, above the modals stacked in it. */
  AboveModals = "AboveModals",
}

export interface TeamsGalleryDeckLayerSpec {
  /** Cportal container the shell renders into. */
  containerId: string;
  /** z-index for that container element when this shell CREATES it. */
  containerZIndex: number;
  /** The shell's own z-index INSIDE that container. */
  shellZIndex: number;
}

export const TEAMS_GALLERY_DECK_LAYER_SPEC: Record<
  TeamsGalleryDeckLayer,
  TeamsGalleryDeckLayerSpec
> = {
  [TeamsGalleryDeckLayer.Deck]: {
    containerId: GALLERY_DECK_PORTAL_CONTAINER_ID,
    containerZIndex: GALLERY_DECK_Z,
    shellZIndex: GALLERY_DECK_Z,
  },
  [TeamsGalleryDeckLayer.AboveModals]: {
    containerId: PORTAL_ROOT_CONTAINER_ID,
    containerZIndex: PORTAL_ROOT_Z_INDEX,
    shellZIndex: GALLERY_DECK_Z,
  },
};

/**
 * EXPERIMENT — which position source the bottom guide reads.
 *
 * Production is `Settled`: the nav is fed `settledPositionMotion`, written ONLY
 * in `handleCarouselSettled`, so the guide cannot follow a scroll in progress —
 * it steps at the detent. `Live` feeds the deck's live position instead.
 *
 * `Live` is NOT 1:1 sync. The nav's slide carries a 420ms eased transform
 * (`SLIDE_TRANSITION` in TeamsGalleryNav.tsx), which a live source continuously
 * retargets — the result is a following, eased guide. Transition duration is a
 * separate lever, deliberately not changed here.
 */
export enum NavSyncMode {
  Settled = "Settled",
  Live = "Live",
}

export const NAV_SYNC_MODES: readonly NavSyncMode[] = [
  NavSyncMode.Settled,
  NavSyncMode.Live,
];

export const DEFAULT_NAV_SYNC_MODE = NavSyncMode.Settled;

/**
 * EXPERIMENT — mobile card width as a fraction of the layout viewport.
 *
 * `app/layout.tsx` requests a 500px layout viewport, which the browser then
 * scales to the physical screen — so the card already occupied a CONSTANT
 * ~86.48% of every phone. This does not add per-device adaptation; it exposes
 * that constant as a design control instead of leaving it as the residue of
 * `innerWidth / (FACE_WIDTH + 48)` saturating the 0.38-0.92 clamp.
 *
 * The ratio is a width CAP: when the stage height is the tighter constraint the
 * rendered card is narrower than the ratio implies.
 *
 * 0.8648 is `FACE_WIDTH * 0.92 / 500` — the ceiling above which the deckScale
 * clamp saturates and every value renders an identical card, and therefore also
 * the exact width-preserving value (470 x 0.92 = 432.4px).
 */
export const DEFAULT_MOBILE_CARD_WIDTH_RATIO = 0.8648;

/** Full-screen deck overlay shell — fade + unblur enter, reverse exit. */
export const GALLERY_OVERLAY_SHELL_EASE: [number, number, number, number] = [
  0.22, 1, 0.36, 1,
];
export const GALLERY_OVERLAY_SHELL_DURATION_S = 0.58;
export const GALLERY_OVERLAY_SHELL_REDUCED_DURATION_S = 0.01;

/**
 * The show6 interaction language for standalone buttons (deploy, quick bolts,
 * arrows, pills): the hover LIFT + brighten the original `show6-hover-pill`
 * class gave, plus a settle on press — fed to Cbutton's stateContainerStyle so
 * the motion, the ghost hover/press films, and the button's own design all
 * paint on ONE element. Compound-pill SEGMENTS deliberately skip the lift
 * (they'd escape the pill's clip) — their film is the feedback.
 */
export const GALLERY_HOVER_LIFT = {
  hover: {
    transform: "translateY(-3px) scale(1.04)",
    filter: "brightness(1.15)",
  },
  pressed: {
    transform: "translateY(-1px) scale(0.98)",
    filter: "brightness(1.05)",
  },
} as const;

/**
 * The `.show6-hover-*` classes' own transform/filter tween. Ccontainer stamps an
 * inline `transition` (its background/border tween list) on the surface it owns,
 * which overrides the class's stylesheet transition — converted Cbuttons that
 * keep a show6 hover class restore the eased lift by carrying this in their
 * `containerStyle`.
 */
export const SHOW6_HOVER_TRANSITION =
  "transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), filter 0.22s ease";

/** chatteaser3's frosted-glass bevel — a bright top-left edge + soft bottom-right. */
export const GLASS_SHADOW =
  "inset -2px -3px 5px -2px rgba(255,255,255,0.14), inset 1px 1px 1px 0 rgba(255,255,255,0.25)";

/** Outer lift so a card reads as a panel floating in 3D space against the black. */
export const FACE_LIFT_SHADOW =
  "0 50px 130px -40px rgba(0,0,0,0.9), 0 30px 60px -50px rgba(95,243,184,0.25)";

/** The mint corner-glow chatteaser3 paints onto its primary UNITED surface. */
export const MINT_CORNER_GLOW = [
  "radial-gradient(circle at 108% 102%, rgba(95,243,184,0.18) 0%, rgba(95,243,184,0.06) 30%, rgba(95,243,184,0.01) 60%, rgba(0,0,0,0) 80%)",
  "radial-gradient(circle at 4% 3%, rgba(95,243,184,0.16) 0%, rgba(95,243,184,0.05) 34%, rgba(95,243,184,0.01) 68%, rgba(0,0,0,0) 90%)",
].join(", ");

export const FACE_GLASS_BG = "rgba(6, 11, 9, 0.86)";
/** Exact background of the `Mysterious` Ccontainer preset the deck cards use — the
 *  expand shell paints this so the morph from card → fullscreen is seamless. */
export const MYSTERIOUS_BG = "rgba(0, 0, 0, 0.86)";
export const FACE_BORDER = "1px solid rgba(255,255,255,0.05)";
export const HAIRLINE_COLOR = "rgba(255,255,255,0.06)";
export const HAIRLINE = `1px solid ${HAIRLINE_COLOR}`;
export const TEXT_MUTED = "rgba(255,255,255,0.45)";
export const TEXT_FAINT = "rgba(255,255,255,0.28)";
/** Failure text and destructive emphasis on the gallery's dark glass.
 *  `ColorType.Danger` (#780000) is a FILL tone — as text over
 *  `rgba(3,8,6,0.72)` it reads as almost-black, so the surface's danger TEXT
 *  token is the coral the deck already uses for its hottest price tier. */
export const DANGER = "#FF6E6E";

export const FACE_RADIUS = 52;
export const TILE_RADIUS = 18;

/** Vertical-gradient text fill — a subtle white→light sheen (no harsh midpoint,
 *  never fades to a dark/transparent bottom). */
export const TEXT_GRADIENT =
  "linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.78) 100%)";

// --- category filter + real category emblems ----------------------------------

export interface CategoryTab {
  key: TeamListCategory;
  label: string;
  /** null for "All" (no single category glyph). */
  category: TeamCategory | null;
}

export const CATEGORY_TABS: readonly CategoryTab[] = [
  { key: TEAM_LIST_CATEGORY.All, label: "All", category: null },
  { key: TEAM_LIST_CATEGORY.Ops, label: "Ops", category: TeamCategory.OPS },
  {
    key: TEAM_LIST_CATEGORY.Cortex,
    label: "Cortex",
    category: TeamCategory.CORTEX,
  },
  {
    key: TEAM_LIST_CATEGORY.Vitals,
    label: "Vitals",
    category: TeamCategory.VITALS,
  },
  {
    key: TEAM_LIST_CATEGORY.Arcade,
    label: "Arcade",
    category: TeamCategory.ARCADE,
  },
];

/** Full category emblem — the same artwork the team gallery modal renders. */
export const CATEGORY_EMBLEM: Record<TeamCategory, string> = {
  [TeamCategory.OPS]: "/icons/ops-full.svg",
  [TeamCategory.CORTEX]: "/icons/cortex-full.svg",
  [TeamCategory.VITALS]: "/icons/vitals-full.svg",
  [TeamCategory.ARCADE]: "/icons/arcade-full.svg",
};

/** Two-tone eyebrow word for a team's category. */
export const CATEGORY_LABEL: Record<TeamCategory, string> = {
  [TeamCategory.OPS]: "OPS",
  [TeamCategory.CORTEX]: "CORTEX",
  [TeamCategory.VITALS]: "VITALS",
  [TeamCategory.ARCADE]: "ARCADE",
};

// --- team-card layout variants ------------------------------------------------

export enum TeamCardVariant {
  Editorial = "Editorial",
  Roster = "Roster",
  Emblem = "Emblem",
  Minimal = "Minimal",
}

export const TEAM_CARD_VARIANTS: readonly TeamCardVariant[] = [
  TeamCardVariant.Editorial,
];

export const DEFAULT_TEAM_CARD_VARIANT = TeamCardVariant.Editorial;

// --- morph source (what the expanded overlay morphs FROM) ----------------------

/** The landing hero card's replica payload — the flight renders the REAL
 *  Cteamcard face (the hero card is not a deck TeamCardContent variant). */
export interface MorphSourceHeroCard {
  teamData: TeamData;
  /** The exact container preset used by the clicked hero card surface. */
  cardContainerPreset?: ContainerPreset;
  /** The hero carousel's card-surface background override. */
  cardBackground: string;
  /** The hero carousel's inner-content backdrop blur override. */
  contentBackdropBlur: number;
}

/**
 * The clicked source card's rect + visual identity — every non-deck morph
 * entry passes one so the expand/collapse flight is source-faithful.
 * null fields = the centre-grow / deck-face defaults.
 */
export interface MorphSource {
  /** The clicked card's viewport rect (null = centre grow). */
  rect: DOMRect | null;
  /** The clicked card's corner radius — the collapsed-end radius of the morph
   *  (null = the deck face's FACE_RADIUS). */
  radius: number | null;
  /** Deck-face variant when the source is a gallery deck-style card. */
  cardVariant: TeamCardVariant | null;
  /** Set when the source is the landing hero carousel card. */
  heroCard: MorphSourceHeroCard | null;
}

/** A rect-only morph source — hosts that have a click rect but no card face. */
export function buildRectMorphSource(rect: DOMRect | null): MorphSource {
  return { rect, radius: null, cardVariant: null, heroCard: null };
}

// --- collection filter (which body of teams) ----------------------------------
// The enum itself lives in the dependency-free gallery.collection leaf so the
// pure contract layer can speak it without this module's React graph; it is
// re-exported here because every rendering host reads the gallery vocabulary
// from this file.

export { TeamsGalleryCollection };

export interface CollectionTab {
  key: TeamsGalleryCollection;
  label: string;
  hint: string;
}

export const COLLECTION_TABS: readonly CollectionTab[] = [
  {
    key: TeamsGalleryCollection.Selection,
    label: "CABAL Selection",
    hint: "Hand-picked public teams",
  },
  {
    key: TeamsGalleryCollection.Owned,
    label: "Owned Collection",
    hint: "Teams you've saved",
  },
];

// --- card enter animation variants --------------------------------------------

export enum EnterVariant {
  Rise = "Rise",
  Scale = "Scale",
  Blur = "Blur",
  Deal = "Deal",
  Flip = "Flip",
}

export const ENTER_VARIANTS: readonly EnterVariant[] = [EnterVariant.Rise];

export const DEFAULT_ENTER_VARIANT = EnterVariant.Rise;

/**
 * Each enter variant as a framer-motion variant pair. A card animates hidden →
 * visible on mount; on a filter change it animates visible → hidden — so the card's
 * EXIT is exactly the chosen ENTER animation played in reverse. All travel is
 * deliberately small so the entrance reads as a settle, not a flourish.
 */
export const ENTER_MOTION: Record<EnterVariant, Variants> = {
  [EnterVariant.Rise]: {
    hidden: { opacity: 0, y: 22 },
    visible: { opacity: 1, y: 0 },
  },
  [EnterVariant.Scale]: {
    hidden: { opacity: 0, scale: 0.93 },
    visible: { opacity: 1, scale: 1 },
  },
  [EnterVariant.Blur]: {
    hidden: { opacity: 0, filter: "blur(8px)", y: 12 },
    visible: { opacity: 1, filter: "blur(0px)", y: 0 },
  },
  [EnterVariant.Deal]: {
    hidden: { opacity: 0, x: -36, rotate: -3 },
    visible: { opacity: 1, x: 0, rotate: 0 },
  },
  [EnterVariant.Flip]: {
    hidden: { opacity: 0, rotateX: 16 },
    visible: { opacity: 1, rotateX: 0 },
  },
};

/** Per-card enter/exit timing; `delay` is applied per card for a centre-first stagger. */
export const CARD_MOTION_TRANSITION = {
  duration: 0.46,
  ease: [0.22, 1, 0.36, 1],
} as const;
export const CARD_MOTION_STAGGER = 0.04;

/**
 * The deck wrapper orchestrates the cards: it only transitions hidden ↔ visible on
 * a real filter change (its key = collection:category). The cards inherit that state
 * by variant propagation — so a card that re-mounts mid-deck (data settling, a refetch)
 * just appears at the parent's current state WITHOUT re-playing the enter animation.
 */
export const DECK_VARIANTS: Variants = { hidden: {}, visible: {} };

// --- 3D coverflow geometry ----------------------------------------------------

export const FACE_WIDTH = 470;
export const FACE_HEIGHT = 568;
export const PERSPECTIVE = 1700;

/**
 * Coverflow placement, in two zones so the deck reads as a real cover-flow:
 *  - near zone (|delta| <= 1): eases the active card + its first neighbour out.
 *  - deck zone (|delta| > 1): clusters further cards into a tighter, more tilted,
 *    receding side stack (rather than flinging them linearly off-screen), so the
 *    ±2 cards stay clearly visible — five cards on screen at rest.
 */
export const CF_NEAR_OFFSET_X = 300; // ±1 translateX (px)
export const CF_FAR_STEP_X = 172; // extra translateX per deck step
export const CF_NEAR_TILT = 40; // ±1 rotateY (deg)
export const CF_FAR_TILT = 12; // extra rotateY per deck step
export const CF_MAX_TILT = 66; // rotateY clamp
export const CF_NEAR_DEPTH = 140; // ±1 translateZ pushback (px)
export const CF_FAR_DEPTH = 110; // extra translateZ per deck step
export const CF_NEAR_SCALE = 0.12; // ±1 scale loss
export const CF_FAR_SCALE = 0.09; // extra scale loss per deck step
export const CF_MIN_SCALE = 0.58;

// --- card collision avoidance: locked to Soft -----------------------------------
// A local detour around the half-step where adjacent cards overlap most — the
// locked Soft profile: a small horizontal clearance bump shaped by
// `cardAvoidanceEnvelope`, no vertical arc.
export const CARD_AVOIDANCE_LATERAL_PX = 48;
const CARD_AVOIDANCE_ENVELOPE_SCALE = 16;

// --- fade + spacing (slider-tunable; all driven by the one `delta` per card) ---

/** Card content fades to 0 by this fraction of the surface fade point — content
 *  disappears before the glass does, so off-centre text never clutters the deck. */
export const CONTENT_FADE_RATIO = 0.45;

export const DEFAULT_SPREAD = 1.15; // multiplies the horizontal gap between cards
export const DEFAULT_FADE_POINT = 3; // |delta| at which a card's surface reaches 0 opacity
export const SPREAD_RANGE = { min: 0.7, max: 2, step: 0.05 } as const;
export const FADE_POINT_RANGE = { min: 1.5, max: 6, step: 0.25 } as const;

/** Bottom-nav labels only render within this |delta| (they're masked past the bar edges). */
export const NAV_RENDER_WINDOW = 6;

// --- bottom nav (chatteaser3-mobile centred carousel) -------------------------

export const NAV_ITEM_SPREAD = 240; // px between adjacent team-name labels

// --- scroll-to-expand interaction ---------------------------------------------

export const EXPAND_PAD_X = 34; // padded-fullscreen horizontal inset (phase A target)
export const EXPAND_PAD_Y = 30; // padded-fullscreen vertical inset
/** Top breathing room inside the expanded team panel (content inset + mobile
 *  picked-member framing). Keep pick-scroll / sticky offsets in sync with this. */
export const EXPANDED_CONTENT_TOP_INSET = 40;
export const CENTERED_EPS = 0.06; // |position - round| under which a card counts as centred

// --- native vertical transport (the scroll-to-expand rebuild) -------------------
// The expand choreography rides a transparent NATIVE vertical snap scroller with
// exactly three stops (deck / padded / full-bleed). `scrollTop / STEP` IS
// `expandProgressMotion`; the scrollend detent commit is the only scroll-driven
// React state.

/** Scroll distance per expand phase — detents sit at 0 / STEP / 2·STEP. */
export const GALLERY_TRANSPORT_STEP_PX = 440;
/** Programmatic-flight settle: the gallery's original settle curve. */
export const GALLERY_FLIGHT_DURATION_S = 0.34;
export const GALLERY_FLIGHT_EASE: readonly [number, number, number, number] = [
  0.22, 1, 0.36, 1,
];
/** Fallback detent-commit debounce when the browser lacks `scrollend`. */
export const GALLERY_TRANSPORT_SETTLE_DEBOUNCE_MS = 160;
/** A lone mouse-wheel tick cannot outrun mandatory snap-back at STEP scale, and
 *  a huge single tick blows through `scroll-snap-stop: always` — so Y-dominant
 *  wheel events at/above this delta step exactly ONE detent through the normal
 *  flight path (passive assist). Trackpad pan streams sit far below it and stay
 *  fully native. */
export const GALLERY_WHEEL_TICK_MIN_DELTA_PX = 60;
/** A tick refused mid-carousel-settle (diagonal X-latch) retries on settle;
 *  intent older than this is stale and dropped. */
export const GALLERY_PENDING_ASSIST_TTL_MS = 500;

// --- the bottom member rail: RETIRED with the Editorial content shape ------------
// The rail was the only place Editorial put members, and the fullscreen member
// detail was the only thing the rail opened. With Editorial dismissed neither
// had a mount point left, so the rail's five card designs, the detail, and the
// member-flight state that drove them are gone. Members now live in the right
// panel and its dock (see `MembersPanel` / `MembersDock`), and the dock's pill
// is `BarCard`, which survives in `gallery.members.tsx` as the dock's card.

// --- team expanded content: locked to Spotlight/DossierDots --------------------

export enum TeamContentVariant {
  /** EVERY member as a medium feature card with a story teaser. */
  SpotlightGallery = "Spotlight/Gallery",
  /** Every member tall: story teaser + trait chips. */
  SpotlightDossier = "Spotlight/Dossier",
  /** Every member with mini stat bars + traits. */
  SpotlightBoard = "Spotlight/Board",
  /** Dossier refinement — scrollable chip row, dot-prefixed chips, 3-line story. */
  SpotlightDossierDots = "Spotlight/DossierDots",
  /** Dossier refinement — scrollable chip row, tinted icon-less chips, 3-line story. */
  SpotlightDossierTint = "Spotlight/DossierTint",
}

export const DEFAULT_TEAM_CONTENT_VARIANT =
  TeamContentVariant.SpotlightDossierDots;

// --- pick-enlarge animation: locked to Morph (non-uniform FLIP stretch) --------

export enum EnlargeVariant {
  /** Non-uniform FLIP morph — stretches to fill (the original). */
  Morph = "Morph",
  /** Uniform scale from the source centre + fade — no stretch. */
  Uniform = "Uniform",
  /** Panel at full size immediately, clip-path unmasks from the source rect. */
  Reveal = "Reveal",
  /** No geometric link — the panel rises in softly. */
  Rise = "Rise",
}

export const DEFAULT_ENLARGE_VARIANT = EnlargeVariant.Morph;

// --- quick-starts presentation: locked to Ticker (vertical slide on a timer) ----

// --- text entrance: locked to Ctextfx Decrypt (Plain on mobile) ----------------

// --- model profile (expanded member): locked to the Plate/Twin/Dial body --------

// --- collapsed (folded) model profile: locked to Pips/Badge/Flip ----------------
// The folded plate always renders pip-row vitals with the swapped badge
// identity: the FAMILY carries the large gradient line, the model name folds
// into the tinted chip beside it (gallery.panel's MiniPlateIdentity).

// --- vitals display: locked to Bars (one mini horizontal bar per vital) -----------

// --- team-edit fields layout: locked to Rows (hairline-seam rows) ------------------

// --- session start widget: locked to Rail/Inset/Lights (armed race lamps) --------
// Alternate compositions remain in `gallery.start.*.tsx` for the collection
// registry; production always renders `RailInsetLightsStartWidget`.

/** Locked leading treatment of the shared Start/Save action line. */
export enum RailBackVariant {
  SegmentWordSmokeGlassHandoff =
    "Segment/Word/Smoke/ChevronPlay/GlassHandoff",
}

export const DEFAULT_RAIL_BACK_VARIANT =
  RailBackVariant.SegmentWordSmokeGlassHandoff;

/** Chevron choreography inside the expanded Start segment. The Surge family
 *  answers the note that the chevrons read as decoration: each Surge member is
 *  a DIRECTED build — motion with an arrow in time — rather than an in-place
 *  oscillation. */
export enum RailExpansionVariant {
  ChevronChase = "Chevron/Chase",
  ChevronWake = "Chevron/Wake",
  ChevronCadence = "Chevron/Cadence",
  ChevronSlipstream = "Chevron/Slipstream",
}

export const DEFAULT_RAIL_EXPANSION_VARIANT =
  RailExpansionVariant.ChevronChase;

/** The Start-only idle readiness cue. Save is deliberately still. The Cue
 *  family adapts the session screen's presence vocabulary — how a working
 *  member's pill or an unread message says "live" — to the resting segment;
 *  each member's doc comment in `gallery.start.rail.tsx` names its source
 *  component, its ease, and its reduced-motion still frame. */
export enum RailPulseVariant {
  TidalBloom = "Aura/TidalBloom",
  /** Fine mint motes loft up through the segment (SessionMemberWorkingCue's
   *  Accent sparks/Fine loft). */
  CueFineLoft = "Cue/FineLoft",
  /** A breathing radial halo anchored at the trailing icon end
   *  (SessionMemberWorkingCue's icon halo). */
  CueBreathingHalo = "Cue/BreathingHalo",
}

export const DEFAULT_RAIL_PULSE_VARIANT = RailPulseVariant.TidalBloom;

/** One-second replacement treatment shown in the opposite toggle's seat —
 *  locked to QuietWord (the bare quiet word, no chip material under it). */
export enum RailToggleRevealVariant {
  QuietWord = "Handoff/QuietWord",
}

export const DEFAULT_RAIL_TOGGLE_REVEAL_VARIANT =
  RailToggleRevealVariant.QuietWord;

/** How the team-edit action line's MIDDLE names what is being edited — the
 *  ratified "Saving a copy" / "Mission team" / "Saved team" reading between
 *  the back segment and Save. Five treatments that differ in KIND: bare word,
 *  eyebrow stack, glass chip, mint signal chip, and a hairline-seamed leading
 *  segment. Geometry law for every member: the reading fits the 100px worst
 *  case (316px column − 24px trough padding − 80px back − 112px Save) without
 *  ellipsis at its longest ratified string, "Saving a copy". */
export enum RailEditContextVariant {
  QuietWord = "EditContext/QuietWord",
  EyebrowStack = "EditContext/EyebrowStack",
  GlassChip = "EditContext/GlassChip",
  SignalChip = "EditContext/SignalChip",
  LeadSegment = "EditContext/LeadSegment",
}

export const DEFAULT_RAIL_EDIT_CONTEXT_VARIANT =
  RailEditContextVariant.QuietWord;

/** The folding material used while the shared rail overlaps one-column content. */
export enum RailFoldSurfaceVariant {
  Frosted = "Frosted/Fold",
  Obsidian = "Obsidian/Fold",
}

export const DEFAULT_RAIL_FOLD_SURFACE_VARIANT =
  RailFoldSurfaceVariant.Frosted;

/** Responsive inline-space ramps for the single-column team modal. */
export enum ModalInlineInsetVariant {
  Tight = "Tight/Ramp",
}

export const DEFAULT_MODAL_INLINE_INSET_VARIANT =
  ModalInlineInsetVariant.Tight;

export function resolveMorphModalInlineInset(
  _variant: ModalInlineInsetVariant,
  viewportWidth: number,
): number {
  return clamp(42, 31.2 + viewportWidth * 0.0336, 80);
}

export function stackModalInlineInset(
  _variant: ModalInlineInsetVariant,
): string {
  return "clamp(25px, 4vw, 58px)";
}

/** How a members column consumes a mobile/compact stack. Web remains capped. */
export enum MembersColumnWidthVariant {
  Full = "Fluid/Full",
}

export const DEFAULT_MEMBERS_COLUMN_WIDTH_VARIANT =
  MembersColumnWidthVariant.Full;

// --- best-for presentation variant (locked to Chips) --------------------------------

export enum BestForVariant {
  /** The best-for entries split into mint-tinted chips. */
  Chips = "Chips",
}

export const DEFAULT_BEST_FOR_VARIANT = BestForVariant.Chips;

// --- edit-mode design axes ---------------------------------------------------------

/**
 * How ONE editable field is enclosed. The same shell encloses every field the
 * product edits — the team fields on the left column of Edit/Deploy and the
 * member fields inside the member editor — so a field can never be dressed two
 * ways on two surfaces.
 *
 * Every team field renders its live control for as long as the team is in Edit
 * mode, so the enclosure has no read state to distinguish from an open one. What
 * it has to do instead is hold a live control at its own content height without
 * turning nine of them into nine identical boxes.
 *
 * The axis has now closed twice. It opened with four enclosures that differed in
 * KIND — no container at all, a spec-sheet label column, machined nesting, and
 * elevation instead of outline — and the elevation branch won outright. `Gutter`
 * and `Tray` were dismissed in the third round, which leaves the ground and the
 * risen deck:
 *
 * - `Rule` has no container: the control stands on the column ground and a
 *   single hairline closes the row beneath it. It is also the member editor's
 *   FIXED enclosure, because that editor is itself a card and a carded field
 *   inside a card is a nested container.
 * - `Riser/Title` replaces outline with elevation: no border at all, a bright
 *   raised face over a near-contact drop, and an 18px sentence-case heading over
 *   the control instead of a tracked micro-cap.
 * - `Riser/Deck` splits that elevation into TWO PLANES — a header band carrying
 *   the label over a raised deck carrying the control — and is the one branch
 *   still being refined. Its children keep the two-plane premise and change only
 *   what the planes are MADE of: a printed dot ground at the
 *   `show6-edit-dots-void-ember` pitch (`Dots`), a glassy header plate seated on
 *   the deck (`Plate`), that plate frosted over the dot ground (`Vitrine`), and
 *   the widened corner and interval of `Capsule`. `Weave`, `Lintel`, `Bowl`,
 *   `Lantern` and `Loft` were dismissed in the fourth round.
 *
 * THE PLATE'S REDO. Four children keep Plate's measured 83px minimum shell and
 * alter only the header plane: suspended frost, dark slate, a seated inlay, or
 * an etched rule. The comparison stays inside Plate's existing 20px shell / 14px
 * header radii, so the shortest starting-rounds field retains a straight edge.
 *
 * THE VITRINE'S OWN ROUND now keeps only Case, the shaded inlay treatment. Its
 * drawn-rim and depth-only siblings were dismissed; the 28px Case corner still
 * leaves 27px of straight edge on the shortest 83px shell.
 *
 * EVERY DECK CARRIES ITS TOP INSET ON THE SHELL, NOT IN THE BAND — see the note
 * in `gallery.fields.shell.ts`. A field whose label is present-but-undrawn
 * renders no title row at all, and a deck that kept the inset in the band left
 * that field's control flush against the shell's top edge.
 */
export enum FieldShellVariant {
  /** Fixed member-card enclosure; not exposed as a tweak axis. */
  Rule = "Rule",
  /** Locked vitrine: opened geometry in suspended glass over a printed floor. */
  RiserDeckVitrineBloom = "Riser/Deck/Vitrine/Bloom",
}

/**
 * The production enclosure. `Tray` held this until the third round dismissed it,
 * and the pick moved to the deck branch the axis is actually converging on —
 * which is also what the panel's persisted selection carries, so a provider-less
 * host and the landing render the same enclosure.
 */
export const DEFAULT_FIELD_SHELL_VARIANT =
  FieldShellVariant.RiserDeckVitrineBloom;

// --- what a written field's WELL is made of ---------------------------------------

/**
 * The enclosure axis above answers what a FIELD is. This one answers what the
 * written field's own well is made of — the box the author actually types into,
 * which every enclosure has so far handed over to the input's default flat fill.
 *
 * The premise is inlay: a well should read as cut INTO the column, not laid on
 * top of it. That is one dark inset shadow away, and once the well is recessed
 * two things follow that the flat box never needed — a real inner gutter, so the
 * text is not typed against the cut edge, and a soft vertical mask on the
 * scrolling content, so a long brief fades into the lip instead of being
 * guillotined by it.
 *
 * The treatments differ in what the recess is MADE of, never in how deep it is:
 *
 * - `Flat` is the incumbent: the input's own fill, no recess, no mask. It stays
 *   in the set as the thing the other five are read against.
 * - `Inlay` is the plain cut: a dark inset drop under a hairline top lip.
 * - `Stipple` prints an editorial dot field on the well's ground, so an empty
 *   brief reads as ruled paper rather than as a hole.
 * - `Groove` lights the cut from above — a top-lit trough with a bright lip and
 *   a dark floor, the deepest-reading of the five.
 * - `Vellum` warms it: a soft light wash down the face over the inset, with the
 *   widest inner gutter and the longest fade.
 * - `Slate` is the coldest — a near-black recessed face with a mint lip that
 *   lights on focus.
 *
 * THE INLAY'S OWN ROUND, and the defect behind it. The plain cut was sent back
 * with a screenshot: the prose stopped a visible band short of the well's top
 * and bottom edges, and the fade completed inside that band, so a long brief
 * read as a faded line hanging over dead space. The cause was geometric, not
 * cosmetic — the inner gutter was a MARGIN on the text wrapper, which is not
 * the scrollport, so the scrolling content could never enter it. Production
 * systems put that breathing room INSIDE the scroller (`padding-block` on the
 * element that scrolls) and anchor the fade to the scrollport's own edges; the
 * text then travels under the padding and disappears exactly at the lip. That
 * is now what every treatment here does — `Ctextinput.inputElementStyle` exists
 * for it — and the five children differ only in what the EDGE then says, plus
 * the focus signature the screenshot also asked for. None of them changes the
 * fill, the lip depth or the recess vocabulary.
 */
export enum FieldInlayVariant {
  InlayFrameEditorial = "Inlay/Frame/Editorial",
}

export const FIELD_INLAY_VARIANTS: readonly FieldInlayVariant[] = [
  FieldInlayVariant.InlayFrameEditorial,
];

export const DEFAULT_FIELD_INLAY_VARIANT =
  FieldInlayVariant.InlayFrameEditorial;

// --- starting-rounds control ------------------------------------------------------

/**
 * The starting-rounds control, settled on both questions it ever had.
 *
 * WHERE: it rides its field's title row, at the trailing edge, on the same line
 * as the label — a count that small never deserved a row of its own under a
 * heading that says exactly what it counts.
 *
 * WHAT: the STEPPER. The axis ran four treatments — a −/value/+ pill, a
 * spec-sheet numeral with a rocker, a machined ladder of counts, and a typed
 * numeral field — and closed on the pill: rounds move by one, in both
 * directions, and a control that spends a whole ladder on the handful of counts
 * a session actually uses is a ruler standing where a pill does the work.
 *
 * Every surviving enclosure now honours the title-row placement. `Gutter` was
 * the one that could not — its label already WAS a column beside the control —
 * and it left the set with the third round, taking the exception with it.
 */

// --- "more parameters" disclosure -------------------------------------------------

/**
 * The affordance that opens the team form's folded half is a LATCH: a raised
 * centred capsule with its chevron nested in its own disc — a physical catch you
 * press to open the drawer.
 *
 * The form asks nine questions and a first-time team needs four of them; the
 * rest (the initial brief, the quick starts, the run parameters) are a returning
 * author's controls, which sit behind this one disclosure and make it the single
 * most-pressed control on the column. What it is MADE of was its own question,
 * answered four ways that differed in kind — a fold in the paper, a list row, a
 * bare text affordance, an object — and the object won: the most-pressed control
 * on a column has to read as a control from across it, and only the capsule does.
 */

// --- edit-container backdrop: locked to Dots/Void/Ember -------------------------
// The editing card always paints the Ember backdrop — a faint mint grain plus
// a sparse accent-ember scatter over a near-void shade (the
// `show6-edit-dots-void-ember` layer in globals.css, applied by
// gallery.edit's EntityBody).

// --- member-card edit button: locked to the Tinted accent disc --------------------
// The small round edit affordance rendered on member cards (MemberSlot) and on the
// picked-member header (PickedMemberPanel): a translucent member-tint wash disc
// with an accent glyph.

export function editBtnStyle({
  accent,
  size,
}: {
  /** Member accent (colorHex). */
  accent: string;
  /** Disc diameter at this call site (28 slot / 36 picked). */
  size: number;
}): CSSProperties {
  return {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    backgroundColor: `${accent}2b`,
    border: `1px solid ${accent}66`,
    boxShadow: GLASS_SHADOW,
    color: accent,
  };
}

// --- member editor background: locked to the card's own surface ------------------
// The editing card paints the Ember backdrop (`show6-edit-dots-void-ember`,
// applied by gallery.edit's EntityBody) and nothing else. The decorative
// second backdrop layer this section used to carry was retired at its Plain
// setting — it painted nothing in production and its three unused moods sat
// beside the field-shell axis as a second, overlapping enclosure knob.

// --- member view → edit-card open transition: locked to Scale --------------------
// The picked-member view (roster) swaps to the MemberEditCard when a member is
// opened for editing: the incoming view zooms up from small, the outgoing zooms
// past, + fade. Consumed by AnimatePresence(mode="wait") swaps in
// gallery.preflight / TeamsGalleryExpanded — the outgoing view plays `exit`, the
// incoming view plays `initial → animate`. Reduced-motion callers skip the
// wrapper entirely (instant swap).

/** One AnimatePresence child's enter/exit motion for the edit-open swap. */
export interface EditOpenTransitionSpec {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
  transition: Transition;
}

export const EDIT_OPEN_TRANSITION_SPEC: EditOpenTransitionSpec = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.06 },
  transition: {
    duration: 0.26,
    ease: GALLERY_OVERLAY_SHELL_EASE,
  },
};

// --- the modal changing MODE ------------------------------------------------------
//
// The team modal is one surface in five states — View, Edit, Deploy's session
// setup, Deploy's team-edit, and the two standalone overlay hosts that open
// straight into one of them. Moving between those states is not a new animation
// to invent: it is the SAME object changing what it shows, so it is cut from the
// modal's own transition.
//
// What the modal's transition IS: the FLIP morph. `expandGeometry` scrubbed by
// `expandProgressMotion`, settled over GALLERY_FLIGHT_DURATION_S on
// GALLERY_FLIGHT_EASE, with the card replica crossfading into the detail content
// as the geometry lands. The spec below borrows exactly two things from it —
// that curve, and that duration — and spends the duration as an exit plus an
// enter rather than as one continuous flight. Nothing here reads or chains the
// morph's MotionValue graph; the surface documents what chaining cost us.
//
// The axis ran three treatments that differed in KIND — a dissolve through the
// panel's own ground, a push through the focal plane, and a travel along the
// modal's vertical axis — and closed on TRANSPORT: the outgoing mode rises out
// of frame and the incoming one rises in from below, along the axis whose
// scrollTop IS the morph clock. A mode change reads as another notch on the
// same track.

/**
 * The swap costs exactly ONE flight, split. Stated as a fraction of the flight
 * rather than as two numbers so the relationship survives a retune of the morph:
 * a mode change can never again run on a clock the morph does not share, which
 * is precisely what the old 0.48s `mode="wait"` swap did while the host fired a
 * 0.34s `openFull()` beside it.
 *
 * The exit is the shorter half. A mode the user has already left should clear
 * out of the way faster than its replacement settles in.
 */
export const MODE_SWAP_EXIT_FRACTION = 0.38;
export const MODE_SWAP_EXIT_DURATION_S =
  GALLERY_FLIGHT_DURATION_S * MODE_SWAP_EXIT_FRACTION;
export const MODE_SWAP_ENTER_DURATION_S =
  GALLERY_FLIGHT_DURATION_S - MODE_SWAP_EXIT_DURATION_S;

const MODE_SWAP_ENTER_TRANSITION: Transition = {
  duration: MODE_SWAP_ENTER_DURATION_S,
  ease: [...GALLERY_FLIGHT_EASE],
};

const MODE_SWAP_EXIT_TRANSITION: Transition = {
  duration: MODE_SWAP_EXIT_DURATION_S,
  ease: [...GALLERY_FLIGHT_EASE],
};

/** One AnimatePresence child's enter/exit for a mode change. Each target
 *  carries its own transition, so the two halves keep their own durations
 *  without the call site having to know there are two. */
export interface ModeSwapSpec {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
}

// Pure translation on the transport's axis. The exit travels less than the
// enter so the pair reads as one continuous upward move rather than as two
// passes in opposite directions.
export const MODE_SWAP_SPEC: ModeSwapSpec = {
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0, transition: MODE_SWAP_ENTER_TRANSITION },
  exit: { opacity: 0, y: -18, transition: MODE_SWAP_EXIT_TRANSITION },
};

/** Reduced motion: the swap is instant — the module's existing fallback, not a
 *  shortened animation. */
export const MODE_SWAP_REDUCED_SPEC: ModeSwapSpec = {
  initial: { opacity: 1 },
  animate: { opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 1, transition: { duration: 0 } },
};

// --- what a scroller's edges may do -----------------------------------------------
//
// Every pane in this module is a `Cscrollable`, and until now each call site
// decided its own fades by hand: seven literals, six of them zero, which is how
// the edit contexts ended up with no faded edges at all while the details view
// kept them. The ROLE is the seam instead — a pane declares what it holds, and
// the profile follows from that.
//
// Two things about `Cscrollable` decide these numbers, and one of them is
// misleadingly named:
//
// - On a VERTICAL scroller `fadeSize` never reaches the mask. The vertical mask
//   is built entirely from the three `verticalFade*` inputs. What `fadeSize`
//   actually does there is set the scroll element's own `padding-bottom` — the
//   trailing room the bottom veil gets to eat BEFORE it reaches content. That is
//   why a form pane carries one at all, and why a pane whose own style already
//   sets `padding-bottom` (the roster, the mobile stacks) is unaffected by it.
// - `verticalFadeMiddleOpacity` is a reading vignette, not an edge: once a top
//   fade shows, the whole body ramps from that alpha up to 1 at the bottom. Over
//   prose it is the affordance. Over a live form it is a permanent dimming of
//   the controls the author is working in — so every role that holds fields
//   pins it to 1 and keeps only the two edges.
//
// The edge sizes are then bounded by the form's own geometry. Fields sit 30px
// apart, so a veil shorter than that gutter can never cover a whole control; and
// because the mask's top and bottom bands are each clamped to how much is
// actually hidden in that direction, the first and last field are never veiled
// at all. The one case that geometry does not cover is the browser scrolling a
// tabbed-to field flush against an edge, and {@link scrollerFadeSafeArea} is
// what answers it.
export enum ScrollerFadeRole {
  /** Long-form reading: the details view's left pane. */
  Prose = "Prose",
  /** A live form column — details Edit, Deploy's team-edit column. */
  Form = "Form",
  /** The members roster pane, whose cards fly to and from the dock. */
  Roster = "Roster",
  /** Mobile's single stack, which holds prose, form and roster at once. */
  Stack = "Stack",
}

/** The four `Cscrollable` fade inputs a role fixes — spreadable onto the pane. */
export interface ScrollerFadeProfile {
  /** On a vertical pane this is trailing `padding-bottom`, NOT a mask input. */
  fadeSize: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
}

export const SCROLLER_FADE_PROFILE: Record<
  ScrollerFadeRole,
  ScrollerFadeProfile
> = {
  // `Cscrollable`'s own defaults. This is the one pane whose fades were already
  // alive and wanted, so the policy adopts its numbers rather than restating
  // them differently and drifting.
  [ScrollerFadeRole.Prose]: {
    fadeSize: 40,
    verticalFadeTopSize: 70,
    verticalFadeBottomSize: 100,
    verticalFadeMiddleOpacity: 0.77,
  },
  // Shorter than the 30px inter-field gutter, and no body vignette at all.
  [ScrollerFadeRole.Form]: {
    fadeSize: 24,
    verticalFadeTopSize: 20,
    verticalFadeBottomSize: 24,
    verticalFadeMiddleOpacity: 1,
  },
  // Cards, not lines: the veil can be longer here without ever half-erasing a
  // word, and the pane is the one place where "there are more members below"
  // is the whole message. Bottom-weighted for that reason.
  [ScrollerFadeRole.Roster]: {
    fadeSize: 28,
    verticalFadeTopSize: 24,
    verticalFadeBottomSize: 36,
    verticalFadeMiddleOpacity: 1,
  },
  // One scroller holding prose AND form AND roster, so it inherits the form's
  // safety: two edges, no vignette. A touch longer than the desktop form pane
  // because the stack IS the whole screen and a phone's edge needs the depth.
  [ScrollerFadeRole.Stack]: {
    fadeSize: 28,
    verticalFadeTopSize: 22,
    verticalFadeBottomSize: 30,
    verticalFadeMiddleOpacity: 1,
  },
};

/**
 * The scrollport inset that keeps the veil off whatever the browser scrolls to.
 *
 * `scroll-padding` is what `scrollIntoView` — and the automatic scroll a browser
 * performs when focus moves to an off-screen control — treats as the edge of the
 * useful region. Without it, tabbing to a field below the fold parks that field
 * flush against the bottom edge, which is exactly where the bottom veil is, and
 * the caret lands in a field dissolving into the panel. With it, the field lands
 * one veil-height clear and is fully opaque. This is the whole reason the edit
 * contexts can carry fades at all.
 */
export function scrollerFadeSafeArea(role: ScrollerFadeRole): CSSProperties {
  const profile = SCROLLER_FADE_PROFILE[role];
  return {
    scrollPaddingTop: `${profile.verticalFadeTopSize}px`,
    scrollPaddingBottom: `${profile.verticalFadeBottomSize}px`,
  };
}

// --- the save action's material ---------------------------------------------------
// Frosted dark glass, a single mint rim, and a tinted glyph disc — declared once
// so the entity card's own commit control and the surface's persistent save
// ledge are made of the same thing and cannot drift apart.

export const SAVE_PILL_CONTAINER_STYLE: CSSProperties = {
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: MINT,
  backgroundImage:
    "linear-gradient(180deg, rgba(13,21,17,0.94) 0%, rgba(6,11,9,0.94) 100%)",
  border: "1px solid rgba(95,243,184,0.4)",
  boxShadow: `${GLASS_SHADOW}, 0 14px 34px -14px rgba(95,243,184,0.5)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  transition: SHOW6_HOVER_TRANSITION,
};

export const SAVE_PILL_GLYPH_DISC_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "50%",
  backgroundColor: "rgba(95,243,184,0.15)",
  border: "1px solid rgba(95,243,184,0.35)",
  flexShrink: 0,
};

/**
 * A field label has to clear 4.5:1 on the panel ground, which the surface's
 * muted token does not; secondary field text reads off this instead.
 */
export const FIELD_LABEL_COLOR = "rgba(255,255,255,0.62)";

/**
 * The team-save error element (the action rail's inline error line). The field
 * that a rejected save names points its group at this id, and the two live in
 * different subtrees, so the id is declared where both can reach it rather
 * than in either one.
 */
export const TEAM_SAVE_ERROR_ELEMENT_ID = "team-save-error";

// --- loading skeleton: locked to Resolve ------------------------------------------
// Faded placeholder text decrypts left→right into place, fading up in opacity,
// then loops (gallery.skeletons.tsx renders it directly — no variant axis).

/** Full-bleed expandProgress at/above which the bottom member rail is shown. */
export const RAIL_REVEAL_AT = 1.9;

// --- member dock (members-right content variants) ------------------------------

/** expandProgress at which the dock starts fading in — visible from padded mode on. */
export const DOCK_REVEAL_AT = 0.85;
/** Viewport band reserved for the dock rail (Bar pill height + breathing room). */
export const DOCK_ZONE_HEIGHT = 104;
// DOCK_HYSTERESIS (the inline ↔ docked anti-flap margin) lives in gallery.dock
// with the membership predicate it parameterizes, keeping the standalone
// gallery.dock.assert script off this module's runtime import graph.

// --- pure math helpers --------------------------------------------------------

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Quartic bump: 0 on the existing trajectory at distance 0 and >=1, with a
 *  smooth peak of 1 at the half-step collision point. Multiplication-only so it
 *  can run inside the existing per-card transform calculation. */
export function cardAvoidanceEnvelope(distance: number): number {
  if (distance <= 0 || distance >= 1) return 0;
  const midpointProduct = distance * (1 - distance);
  return CARD_AVOIDANCE_ENVELOPE_SCALE * midpointProduct * midpointProduct;
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

// --- continuous-position math (pure, N-item) ----------------------------------

/** Signed distance to item `index`, wrapped to its nearest copy: range (-count/2, count/2]. */
export function wrapDelta(raw: number, count: number): number {
  if (count <= 1) return 0;
  let delta = ((raw % count) + count) % count;
  if (delta > count / 2) delta -= count;
  return delta;
}

/**
 * Approx on-screen X (px from the deck centre) of a card at distance `delta` —
 * its coverflow translateX, foreshortened by its translateZ through the perspective.
 * Used by the flat click layer to map a click to the nearest card (3D-transformed
 * cards aren't reliably hit-tested, so we resolve the target geometrically).
 */
export function approxScreenX({
  delta,
  spread,
}: {
  delta: number;
  spread: number;
}): number {
  const direction = Math.sign(delta);
  const dist = Math.abs(delta);
  const near = Math.min(dist, 1);
  const far = Math.max(0, dist - 1);
  const avoidance = cardAvoidanceEnvelope(dist);
  const translateX =
    direction * (CF_NEAR_OFFSET_X * near + CF_FAR_STEP_X * far) * spread +
    direction * CARD_AVOIDANCE_LATERAL_PX * avoidance;
  const translateZ = -(CF_NEAR_DEPTH * near + CF_FAR_DEPTH * far);
  return translateX * (PERSPECTIVE / (PERSPECTIVE - translateZ));
}

// --- production variant bundle -------------------------------------------------
// THE locked design — the saved tweak-panel selections (dev/tweaks/screens/show6
// data.json) promoted into constants. Every host of the gallery surfaces (the
// deck, the standalone team overlay, the preflight overlay, the session member
// overlay) renders from THIS bundle, so no host can drift from the selected
// variants again. The tweaks panel on /dev/show6 initializes from these same
// defaults and stays the only place variants are ever changed.

export interface TeamsGalleryVariantBundle {
  cardVariant: TeamCardVariant;
  bestForVariant: BestForVariant;
}

export const GALLERY_PRODUCTION_VARIANTS: TeamsGalleryVariantBundle = {
  cardVariant: DEFAULT_TEAM_CARD_VARIANT,
  bestForVariant: DEFAULT_BEST_FOR_VARIANT,
};
